import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import { demirCaplari, demirKaliteleri } from '@mocks/materials/demir';
import { mockCompanies } from '@mocks/firmalar';
import { calculateKDV, DEFAULT_KDV_RATE } from '@utils/kdv';
import { TEDARIK_KAYNAKLARI, stokYeterliMi } from '@utils/stok';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import KDVSummary from '../../components/KDVSummary';

/**
 * Props:
 *   onSubmit(data, isEdit) — Normal malzeme sayfasından kullanım
 *   onMaterialSubmit(enrichedData) — Finance modundan açıldığında (PlanliOdemeModal/MaterialModalRouter)
 *   submitLabel — Buton yazısı ("Sipariş Oluştur" default, "Hesapla & Dön" finance modunda)
 */
export default function DemirModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );

    const { register, handleSubmit, reset, watch } = useForm({
        defaultValues: initialData || {
            siparisTarihi: new Date().toISOString().split('T')[0],
            cap: 'Ø12',
            kalite: 'B420C',
            birim: 'ton',
            kdvOrani: DEFAULT_KDV_RATE,
            tedariKaynagi: 'tedarikci',
        },
    });

    const watchedBirimFiyat = watch('birimFiyat');
    const watchedMiktar = watch('miktar');
    const watchedKdvOrani = watch('kdvOrani');
    const watchedKaynak = watch('tedariKaynagi');
    const watchedCap = watch('cap');

    // Stoktan aktar seçildiğinde stok kontrolü
    const stokHavuzuDemir = getStokHavuzu('demir');
    const mevcutStok = stokHavuzuDemir.find(s => s.altTip === watchedCap);
    const serbestStok = mevcutStok?.serbestStok || 0;
    const miktarNum = parseFloat(watchedMiktar) || 0;
    const stokYeterli = stokYeterliMi(serbestStok, miktarNum);

    const handleFormSubmit = (data) => {
        if (data.tedariKaynagi === 'stoktan') {
            if (!stokYeterli) return;
            data.birimFiyat = 0;
            data.toplamFiyat = 0;
            data.kdvOrani = 0;
            data.kdvTutari = 0;
            data.kdvDahilToplam = 0;
            data.tedarikci = null;
            data.supplierId = null;
        } else {
            const result = calculateKDV({ unitPrice: data.birimFiyat, quantity: data.miktar, vatRate: data.kdvOrani });
            data.toplamFiyat = result.toplamHaric;
            data.kdvOrani = result.kdvOrani;
            data.kdvTutari = result.kdvTutari;
            data.kdvDahilToplam = result.toplamDahil;
            // Seçilen firmayı veriye ekle
            data.tedarikci = selectedCompany?.name || data.tedarikci;
            data.supplierId = selectedCompany?.id || null;
        }
        data.stokMiktar = data.stokMiktar || data.miktar;
        data.kullanilanMiktar = data.miktar - data.stokMiktar;

        const enriched = {
            ...data,
            selectedCompany,
            calculatedTotal: data.kdvDahilToplam,
            autoDescription: `Demir Alımı — ${data.miktar} ton ${data.cap}`,
        };

        if (onMaterialSubmit) {
            // Finance modundan açıldı → hesapla & geri dön
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const isStoktan = watchedKaynak === 'stoktan';
    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50";

    return (
        <Modal isOpen={isOpen} onClose={() => { reset(); onClose(); }} title={isEdit ? 'Demir Siparişi Düzenle' : 'Yeni Demir Siparişi'} size="lg"
            footer={<><Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading} disabled={isStoktan && !stokYeterli}>{submitLabel || (isEdit ? 'Güncelle' : 'Sipariş Oluştur')}</Button></>}>
            <form className="space-y-5">
                {/* Tedarik Kaynağı */}
                <div className="p-3 bg-bg-tertiary rounded-lg">
                    <label className="block text-sm font-medium text-text-primary mb-2">Tedarik Kaynağı <span className="text-error">*</span></label>
                    <div className="flex gap-3">
                        {TEDARIK_KAYNAKLARI.map(tk => (
                            <label key={tk.key} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${watchedKaynak === tk.key ? 'border-primary bg-primary/5 text-primary' : 'border-border-light text-text-secondary hover:bg-bg-hover'}`}>
                                <input type="radio" {...register('tedariKaynagi')} value={tk.key} className="sr-only" />
                                <span className="text-sm font-medium">{tk.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Stoktan uyarı */}
                {isStoktan && (
                    <div className={`p-3 rounded-lg border ${stokYeterli || miktarNum === 0 ? 'bg-info/5 border-info/30' : 'bg-error/5 border-error/30'}`}>
                        <p className="text-sm text-text-secondary">
                            <strong>{watchedCap}</strong> çapında serbest stok: <span className="font-bold text-success">{serbestStok} ton</span>
                        </p>
                        {miktarNum > 0 && !stokYeterli && (
                            <p className="text-sm text-error mt-1 font-medium">⚠ Yetersiz stok! İstenen: {miktarNum} ton</p>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <CompanySelector
                            value={selectedCompany}
                            onChange={setSelectedCompany}
                            companies={mockCompanies}
                            label="Tedarikçi / Firma"
                            required={!isStoktan}
                            disabled={isStoktan}
                        />
                    </div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Sipariş Tarihi <span className="text-error">*</span></label>
                        <input type="date" {...register('siparisTarihi', { required: true })} className={inputClass} /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Demir Çapı <span className="text-error">*</span></label>
                        <select {...register('cap', { required: true })} className={inputClass}>{demirCaplari.map(c => <option key={c.key} value={c.key}>{c.label} - {c.kullanim}</option>)}</select></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Kalite</label>
                        <select {...register('kalite')} className={inputClass}>{demirKaliteleri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}</select></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Miktar (ton) <span className="text-error">*</span></label>
                        <input type="number" step="0.1" {...register('miktar', { required: true, valueAsNumber: true })} placeholder="0" className={inputClass} /></div>
                    {!isStoktan && (
                        <>
                            <div><label className="block text-sm font-medium text-text-primary mb-1.5">Birim Fiyat (₺/ton)</label>
                                <input type="number" {...register('birimFiyat', { valueAsNumber: true })} placeholder="25000" className={inputClass} /></div>
                            <div><label className="block text-sm font-medium text-text-primary mb-1.5">KDV Oranı (%)</label>
                                <input type="number" step="1" {...register('kdvOrani', { valueAsNumber: true })} placeholder="20" className={inputClass} /></div>
                        </>
                    )}
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Durum</label>
                        <select {...register('durum')} className={inputClass}><option value="bekliyor">Bekliyor</option><option value="teslim">Teslim Edildi</option></select></div>
                </div>

                {!isStoktan && <KDVSummary unitPrice={watchedBirimFiyat} quantity={watchedMiktar} vatRate={watchedKdvOrani} />}

                <div><label className="block text-sm font-medium text-text-primary mb-1.5">Not</label>
                    <textarea {...register('not')} rows={2} placeholder="Sipariş notu..." className={`${inputClass} resize-none`} /></div>
            </form>
        </Modal>
    );
}
